import { useState, useEffect, useCallback, useRef } from 'react';

const STORAGE_KEY = 'gympro_last_build';
const POLL_INTERVAL = 5 * 60 * 1000; // 5 minutos
const DEFAULT_GRACE_PERIOD = 20 * 1000; // 20 s oculta antes de recargar

interface BuildInfo {
  buildAt: string;
  commit: string;
}

interface UseVersionCheckOptions {
  enabled?: boolean;
  /** Tiempo que la pestaña debe permanecer oculta antes de aplicar la actualización. */
  gracePeriodMs?: number;
}

/**
 * Detecta una nueva versión (vía /version.json) y aplica la actualización de
 * forma silenciosa: recarga la app solo cuando la pestaña queda en segundo
 * plano (tras un periodo de gracia), o al volver a primer plano en móviles
 * que suspenden JS en background (iOS). Nunca se muestra UI ni se interrumpe
 * una tarea en curso.
 */
export function useVersionCheck(opts: UseVersionCheckOptions = {}) {
  const { enabled = true, gracePeriodMs = DEFAULT_GRACE_PERIOD } = opts;
  const [hasUpdate, setHasUpdate] = useState(false);
  const pendingBuildAt = useRef<string | null>(null);
  const graceTimer = useRef<number | null>(null);

  const checkVersion = useCallback(async () => {
    try {
      const res = await fetch(`/version.json?t=${Date.now()}`, {
        cache: 'no-store',
      });

      if (!res.ok) return;

      const current: BuildInfo = await res.json();
      const stored = localStorage.getItem(STORAGE_KEY);

      if (!stored) {
        localStorage.setItem(STORAGE_KEY, current.buildAt);
        return;
      }

      if (current.buildAt !== stored) {
        pendingBuildAt.current = current.buildAt;
        setHasUpdate(true);
      }
    } catch {
      // Silencioso — si falla el fetch, no notificamos
    }
  }, []);

  const clearGraceTimer = useCallback(() => {
    if (graceTimer.current !== null) {
      window.clearTimeout(graceTimer.current);
      graceTimer.current = null;
    }
  }, []);

  const applyUpdate = useCallback(() => {
    // Persistir el build nuevo ANTES de recargar para que la nueva versión
    // no vuelva a detectarse como "actualización pendiente" (evita bucles).
    if (pendingBuildAt.current) {
      localStorage.setItem(STORAGE_KEY, pendingBuildAt.current);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
    window.location.reload();
  }, []);

  useEffect(() => {
    if (!enabled) {
      setHasUpdate(false);
      pendingBuildAt.current = null;
      clearGraceTimer();
      return;
    }

    checkVersion();

    const interval = setInterval(checkVersion, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [enabled, checkVersion, clearGraceTimer]);

  useEffect(() => {
    if (!enabled || !hasUpdate) return;

    let hiddenAt: number | null = null;

    const onVisibilityChange = () => {
      if (document.hidden) {
        hiddenAt = Date.now();
        clearGraceTimer();
        graceTimer.current = window.setTimeout(() => {
          applyUpdate();
        }, gracePeriodMs);
      } else {
        const elapsed = hiddenAt !== null ? Date.now() - hiddenAt : 0;
        hiddenAt = null;
        clearGraceTimer();
        // iOS (y algunos móviles) suspenden JS en background: el timer no
        // corre, así que aplicamos al volver a primer plano si estuvo oculta
        // al menos el periodo de gracia. En desktop el timer ya recargó oculta.
        if (elapsed >= gracePeriodMs) {
          applyUpdate();
        }
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    if (document.hidden) {
      onVisibilityChange();
    }

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      clearGraceTimer();
    };
  }, [enabled, hasUpdate, gracePeriodMs, applyUpdate, clearGraceTimer]);

  const dismiss = useCallback(() => {
    setHasUpdate(false);
  }, []);

  return { hasUpdate, applyUpdate, dismiss };
}
